import "./ContractLinkage.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Button, Typography} from "@mui/material";
import {FileDownloadOutlined} from "@mui/icons-material";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {
    buildContractsById,
    buildTransactions,
    CL_CURRENCY,
    CL_PERIOD,
    CL_SUBTITLE,
    computeClKpis,
    computePortfolioKpis,
    contractRuntimeStatus,
} from "./contractLinkageData";
import type {ClContractEdits, ClDecisions, ClPortfolioFilter, ClReviewTx} from "./contractLinkageUtils";
import {
    effectiveContractId,
    formatCompactCurrency,
    formatNumber,
    formatPct,
    loadContractEdits,
    loadDecisions,
    matchesFilter,
    saveContractEdits,
    saveDecisions,
    statusOf,
    tierOfConfidence,
} from "./contractLinkageUtils";
import {exportContractLinkageCsv} from "./contractLinkageExport";
import {ExplorerStatusCards} from "./components/ExplorerStatusCards";
import {ContractExplorer} from "./components/ContractExplorer";
import {TransactionDrawer} from "./components/TransactionDrawer";
import {ContractPicker} from "./components/ContractPicker";
import {
    useClContractGensQuery,
    useClContractsQuery,
    useClUnmatchedGensQuery,
} from "./useContractLinkageQueries";

export const ContractLinkagePage: React.FC = observer(() => {
    const [decisions, setDecisions] = React.useState<ClDecisions>(loadDecisions);
    const [contractEdits, setContractEdits] = React.useState<ClContractEdits>(loadContractEdits);
    const [statusFilter, setStatusFilter] = React.useState<ClPortfolioFilter | null>(null);

    // Drawer & contract picker
    const [openTxId, setOpenTxId] = React.useState<string | null>(null);
    const [pickerTxIds, setPickerTxIds] = React.useState<string[] | null>(null);

    const {data: rawContracts = [], isPending: contractsLoading} = useClContractsQuery();
    const {data: contractGens = [], isPending: gensLoading} = useClContractGensQuery();
    const {data: unmatchedGens = [], isPending: unmatchedLoading} = useClUnmatchedGensQuery();
    const dataReady = !contractsLoading && !gensLoading && !unmatchedLoading;

    // The contract record as extracted (status derived from its dates)…
    const baseContracts = React.useMemo(
        () => rawContracts.map(c => ({...c, status: contractRuntimeStatus(c)})),
        [rawContracts]);
    const baseContractsById = React.useMemo(() => buildContractsById(baseContracts), [baseContracts]);

    // …and with the user's manual corrections applied (status re-derived, edits may move dates).
    const contracts = React.useMemo(
        () => baseContracts.map(c => {
            const patch = contractEdits[c.id];
            if (!patch) return c;
            const edited = {...c, ...patch};
            return {...edited, status: contractRuntimeStatus(edited)};
        }),
        [baseContracts, contractEdits]);
    const contractsById = React.useMemo(() => buildContractsById(contracts), [contracts]);

    const transactions = React.useMemo(
        () => (dataReady ? buildTransactions(contracts, contractGens, unmatchedGens) : []),
        [dataReady, contracts, contractGens, unmatchedGens],
    );
    const k = React.useMemo(
        () => computeClKpis(transactions, contracts.length),
        [transactions, contracts.length],
    );
    const portfolio = React.useMemo(
        () => computePortfolioKpis(contracts, transactions),
        [contracts, transactions],
    );

    const updateDecisions = (mutate: (next: ClDecisions) => void) => {
        setDecisions(prev => {
            const next = {...prev};
            mutate(next);
            saveDecisions(next);
            return next;
        });
    };

    // Every transaction, decorated with the user's decision applied.
    const rows = React.useMemo<ClReviewTx[]>(() => transactions.map(tx => {
        const decision = decisions[tx.id];
        const effId = effectiveContractId(tx, decision);
        return {
            ...tx,
            status: statusOf(tx, decision),
            tier: tierOfConfidence(tx.confidence),
            effectiveContractId: effId,
            effectiveContractName: effId ? contractsById.get(effId)?.name ?? null : null,
            aiContractName: tx.aiContractId ? contractsById.get(tx.aiContractId)?.name ?? null : null,
        };
    }), [transactions, decisions, contractsById]);

    const rowById = React.useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);

    // ---- Review actions (single & bulk share the same handlers) ----
    const accept = React.useCallback((txIds: string[]) => updateDecisions(next => {
        txIds.forEach(id => {
            if (rowById.get(id)?.aiContractId) next[id] = {action: 'accept'};
        });
    }), [rowById]);

    const assignTo = React.useCallback((contractId: string, txIds: string[]) => updateDecisions(next => {
        txIds.forEach(id => {
            const tx = rowById.get(id);
            if (!tx) return;
            // Assigning back to the AI's own suggestion is an acceptance.
            next[id] = contractId === tx.aiContractId ? {action: 'accept'} : {action: 'assign', contractId};
        });
    }), [rowById]);

    const unlink = React.useCallback((txIds: string[]) => updateDecisions(next => {
        txIds.forEach(id => {
            const tx = rowById.get(id);
            if (!tx) return;
            // Removing a manual link from an AI-unmatched transaction restores "no link".
            if (!tx.aiContractId) delete next[id];
            else next[id] = {action: 'unlink'};
        });
    }), [rowById]);

    const reset = React.useCallback((txIds: string[]) => updateDecisions(next => {
        txIds.forEach(id => delete next[id]);
    }), []);

    const openPicker = React.useCallback((txIds: string[]) => setPickerTxIds(txIds), []);

    const saveContract = React.useCallback((contractId: string, patch: ClContractEdits[string] | null) => {
        setContractEdits(prev => {
            const next = {...prev};
            if (patch) next[contractId] = patch;
            else delete next[contractId];
            saveContractEdits(next);
            return next;
        });
    }, []);

    const reviewedCount = React.useMemo(
        () => rows.filter(tx => matchesFilter(tx, 'reviewed')).length, [rows]);

    const kpis: KpiBoxedValue[] = [
        {
            key: 'portfolio',
            title: 'Contract portfolio',
            subtitle: <>{formatNumber(k.contractCount)}
                <span className="cl-kpi-sub"> · {formatCompactCurrency(portfolio.contractedValue, CL_CURRENCY)} contracted</span></>,
            help: `${formatNumber(portfolio.active)} active, ${formatNumber(portfolio.expiring)} expiring and ` +
                `${formatNumber(portfolio.expired)} expired contracts, extracted from the uploaded documents.`,
        },
        {
            key: 'linked',
            title: 'Spend under contract (12m)',
            subtitle: <>{formatCompactCurrency(k.linkedSpend, CL_CURRENCY)}
                <span className="cl-kpi-sub"> · {formatPct(k.totalSpend > 0 ? k.linkedSpend / k.totalSpend : 0)}</span></>,
            help: `${formatNumber(k.linkedCount)} of ${formatNumber(k.txCount)} transactions were matched to one of ` +
                `${formatNumber(k.contractCount)} contracts (${CL_PERIOD.from} – ${CL_PERIOD.to}).`,
        },
        {
            key: 'offcontract',
            title: 'Off-contract spend (12m)',
            subtitle: <>{formatCompactCurrency(k.unmatchedSpend, CL_CURRENCY)}
                <span className="cl-kpi-sub"> · {formatNumber(k.unmatchedCount)} tx</span></>,
            help: 'Spend with no qualifying contract — including purchases hidden behind marketplaces, ' +
                'payment providers and employee reimbursements. Pull them onto the right contract via ' +
                '“Add transactions”.',
        },
        {
            key: 'progress',
            title: 'Review progress',
            subtitle: <>{formatNumber(reviewedCount)}
                <span className="cl-kpi-sub"> of {formatNumber(k.txCount)} reviewed</span></>,
            help: 'Transactions you accepted, relinked or unlinked. Decisions are kept on this device for the showcase.',
        },
    ];

    const openTx = openTxId ? rowById.get(openTxId) ?? null : null;
    const pickerTxs = (pickerTxIds ?? []).map(id => rowById.get(id)).filter((t): t is ClReviewTx => !!t);

    return <MainPageFacade
        title={CARD_CONFIG['contract-linkage'].title}
        className="contract-linkage-page"
        actionBarEnd={
            <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined/>}
                onClick={() => exportContractLinkageCsv(rows, contractsById)}>
                Export
            </Button>
        }>

        <Typography className="cl-subtitle" variant="body2">
            {CL_SUBTITLE}
        </Typography>

        <KpiBoxedValues values={kpis}/>

        <ExplorerStatusCards
            contracts={contracts}
            rows={rows}
            portfolio={portfolio}
            activeFilter={statusFilter}
            onToggleFilter={key => setStatusFilter(cur => (cur === key ? null : key))}
        />

        <ContractExplorer
            contracts={contracts}
            baseContractsById={baseContractsById}
            edits={contractEdits}
            rows={rows}
            statusFilter={statusFilter}
            onClearStatusFilter={() => setStatusFilter(null)}
            onOpenTx={tx => setOpenTxId(tx.id)}
            onAccept={accept}
            onUnlink={unlink}
            onAssignTo={assignTo}
            onSaveContract={saveContract}
        />

        <Typography className="cl-scope-note" variant="caption">
            Scope: positive third-party spend, {CL_PERIOD.from} – {CL_PERIOD.to} ({CL_PERIOD.label}). Links are proposed
            by the AI matching engine from PO references, contracted rates & terms, and invoicing patterns; your review
            decisions take precedence over the AI suggestion. Contract details are extracted from the uploaded
            documents; your manual corrections override the extracted values.
        </Typography>

        <TransactionDrawer
            tx={openTx}
            contractsById={contractsById}
            onAccept={id => accept([id])}
            onAssign={id => openPicker([id])}
            onAssignTo={(id, contractId) => assignTo(contractId, [id])}
            onUnlink={id => unlink([id])}
            onReset={id => reset([id])}
            onClose={() => setOpenTxId(null)}
        />

        <ContractPicker
            open={!!pickerTxIds}
            contracts={contracts}
            txs={pickerTxs}
            onPick={contractId => {
                if (pickerTxIds) assignTo(contractId, pickerTxIds);
                setPickerTxIds(null);
            }}
            onClose={() => setPickerTxIds(null)}
        />
    </MainPageFacade>;
});
